<template>
  <div>
    <NavDark/>

    <section class="middle" v-if="data" aria-label="Produktdetaljer">
      <div class="container">
        <div class="row">

          <div class="col-xl-5 col-lg-6 col-md-12 col-sm-12">
            <div>
              <inner-image-zoom
                :src="activeTab"
                :zoomSrc="activeTab"
                class="w-100"
                role="img"
                :aria-label="data?.name || 'Produkt billede'"
              />
            </div>

            <div
              v-if="Array.isArray(data.images) && data.images.length && data.images.length > 1"
              class="d-flex flex-wrap gap-2 mt-3"
              aria-label="Flere produktbilleder"
            >
              <a
                v-for="(img, idx) in data.images"
                :key="img + '-' + idx"
                href="#"
                @click.prevent="setActive(img)"
                class="d-inline-block overflow-hidden rounded border"
                :class="{'border-dark': img === activeTab, 'border-2': img === activeTab}"
                style="width:72px;height:72px"
                :title="'Se billede ' + (idx+1)"
                :aria-current="img === activeTab ? 'true' : null"
              >
                <img
                  :src="img"
                  :alt="data?.name ? data.name + ' billede ' + (idx+1) : 'Produkt billede ' + (idx+1)"
                  class="img-fluid w-100"
                  style="object-fit: cover; height: -webkit-fill-available;"
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </div>
          </div>

          <div class="col-xl-7 col-lg-6 col-md-12 col-sm-12">
            <div class="prd_details ps-xl-5">

              <div class="mb-4">
                <div class="prt_01 mb-1" v-if="data?.tag && data?.tag == 'Sold'">
                  <span class="text-light bg-sold rounded px-2 py-1" aria-label="Solgt">Solgt</span>
                </div>
                <h1 class="ft-bold mb-1" v-html="data?.name"></h1>
                <div class="d-flex flex-wrap align-items-center">
                  <div class="me-4">
                    <span v-if="!(data?.tag === 'Sold' && data?.artistSoldOutHidePrice === 1)" class="ft-bold fs-md" :aria-label="data?.price + ' kroner'">{{ data?.price }} Kr.</span>
                    <span v-else class="ft-bold fs-md" aria-label="Solgt produkt">Solgt produkt</span>
                  </div>
                </div>
                <div class="text-muted">
                  Kategori:
                  <strong class="fs-sm text-dark ft-medium ms-1">
                    {{ Categories.find(c => String(c.id) === String(data?.brancheId))?.displayname || 'Ukendt' }}
                  </strong>
                </div>
              </div>

              <div class="prt_03 mb-4">
                <p v-html="data?.description"></p>
              </div>

              <div class="mb-4">
                <div class="d-grid gap-2">

                  <a
                    :href="data?.purchaseLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn custom-height btn-dark"
                    :aria-label="'Se produkt på ' + (data?.purchaseLink ? data.purchaseLink : '')"
                    :title="'Åbn på ' + (data?.purchaseLink ? data.purchaseLink : '')"
                  >
                    <i class="lni lni-link me-2" aria-hidden="true"></i>
                    Se på {{ data?.purchaseLink?.replace(/^https?:\/\//, '').split('/')[0] }}
                  </a>

                  <router-link
                    :to="'/kunstner/' + data?.artistSubdomain"
                    class="btn custom-height btn-default text-dark border"
                    :aria-label="'Se profilside for ' + (data?.artistName || 'kunstner')"
                    :title="'Profilside for ' + (data?.artistName || 'kunstner')"
                  >
                    <i class="lni lni-user me-2" aria-hidden="true"></i> Se profilside
                  </router-link>

                </div>
              </div>

              <router-link
                  :to="'/kunstner/' + data?.artistSubdomain"
                  :aria-label="'Se profilside for ' + (data?.artistName || 'kunstner')"
                  :title="'Profilside for ' + (data?.artistName || 'kunstner')"
              >
                <div class="d-flex align-items-center p-3 border rounded mb-4">
                  <div v-if="data?.artistImage" class="me-3">
                    <img
                      :src="data?.artistImage"
                      class="img-fluid circle"
                      width="80"
                      :alt="data?.artistName ? ('Portræt af ' + data.artistName) : 'Kunstner portræt'"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div class="flex-grow-1">
                    <h2 class="fs-md ft-bold mb-0 lh-1" v-html="data?.artistName"></h2>
                    <span class="text-muted smalls" v-html="data?.artistShortDescription"></span>
                  </div>
                </div>
              </router-link>

            </div>
          </div>

        </div>
      </div>
    </section>

    <FooterTopOne/>
    <FooterDark/>
    <ScrollToTop/>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue' // <-- nextTick removed
import { useRoute } from 'vue-router'
import InnerImageZoom from 'vue-inner-image-zoom'

import productPlaceholder from '@/assets/img/product/15.png'
import { Categories } from '@/data/data'

import NavDark from '@/components/navbar/nav-dark.vue'
import FooterTopOne from '@/components/footer/footer-top-one.vue'
import FooterDark from '@/components/footer/footer-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'

import { useProductStore, mapProduct } from '@/stores/useProductStore'
import {fetchProducts, registerProductView} from '@/services/api'
import { updateMeta } from '@/utils/meta.js'

const route = useRoute()
const store = useProductStore()

const data = ref(null)
const activeTab = ref(productPlaceholder)
const isLoading = ref(false)

const currentId = computed(() => {
  const raw = String(route.params.id ?? '');
  const m = raw.match(/(\d+)$/);
  return m ? m[1] : '';
});

function setActive(src) {
  activeTab.value = src || productPlaceholder
}

function setProduct(p) {
  data.value = p
  const first = Array.isArray(p?.images) && p.images.length ? p.images[0] : null
  setActive(first)

  if (p) {
    updateMeta(p.name + ' - ' + p.artistName, p.description);
  }
}

function findInStore() {
  return Array.isArray(store.productData)
      ? store.productData.find(p => String(p.id) === currentId.value) || null
      : null
}

async function loadProduct() {
  if (!currentId.value) return
  isLoading.value = true

  const existing = findInStore()
  if (existing) {
    setProduct(existing)
    isLoading.value = false
    return
  }

  try {
    const res = await fetchProducts({ product_id: currentId.value })
    if (Array.isArray(res) && res.length > 0) {
      const match = res.find(r => String(r.id) === currentId.value) || res[0]
      if (match) {
        setProduct(mapProduct(match))
      } else {
        data.value = null
      }
    } else {
      data.value = null
    }
  } catch (error) {
    console.error("Failed to load product:", error);
    data.value = null
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadProduct();
  if (currentId.value) registerProductView(currentId.value);
});

watch(() => route.params.id, async () => {
  await loadProduct();
  if (currentId.value) registerProductView(currentId.value);
});

</script>